import { supabase } from '@/integrations/supabase/client';

export interface ChildPrivacySettings {
  profileVisibility: 'public' | 'team-only' | 'private';
  showContactPublic: boolean;
  showAge: boolean;
  allowMessages: boolean;
  allowTeamInvites: boolean;
  showOnlineStatus: boolean;
}

const DEFAULT_CHILD_PRIVACY: ChildPrivacySettings = {
  profileVisibility: 'private',
  showContactPublic: false,
  showAge: false,
  allowMessages: false,
  allowTeamInvites: true,
  showOnlineStatus: false,
};

/**
 * Verify that a parent is connected to a child
 */
export const verifyParentChildConnection = async (
  childId: string,
  parentId: string
): Promise<boolean> => {
  try {
    const { data, error } = await (supabase as any)
      .from('parent_child_connections')
      .select('id')
      .eq('child_id', childId)
      .eq('parent_id', parentId)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error verifying parent-child connection:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error verifying parent-child connection:', error);
    return false;
  }
};

/**
 * Get a child's privacy settings (for parent viewing)
 */
export const getChildPrivacySettings = async (
  childId: string,
  parentId: string
): Promise<{ success: boolean; data?: ChildPrivacySettings; error?: string }> => {
  try {
    // Verify parent-child connection first
    const isConnected = await verifyParentChildConnection(childId, parentId);
    if (!isConnected) {
      return { success: false, error: 'Not authorized to access this child\'s settings' };
    }

    // Get existing privacy settings
    const { data, error } = await (supabase as any)
      .from('user_privacy_settings')
      .select('*')
      .eq('user_id', childId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching child privacy settings:', error);
      return { success: false, error: error.message };
    }

    if (data) {
      return {
        success: true,
        data: {
          profileVisibility: data.profile_visibility || 'private',
          showContactPublic: data.show_email || data.show_phone || false,
          showAge: data.show_age || false,
          allowMessages: data.allow_messages || false,
          allowTeamInvites: data.allow_team_invites !== false,
          showOnlineStatus: data.show_online_status || false,
        },
      };
    }

    // Return default (restrictive) settings for children
    return { success: true, data: DEFAULT_CHILD_PRIVACY };
  } catch (error: any) {
    console.error('Error getting child privacy settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a child's privacy settings (parent only)
 */
export const updateChildPrivacySettings = async (
  childId: string,
  parentId: string,
  settings: ChildPrivacySettings
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Verify parent-child connection first
    const isConnected = await verifyParentChildConnection(childId, parentId);
    if (!isConnected) {
      return { success: false, error: 'Not authorized to update this child\'s settings' };
    }

    // Upsert privacy settings
    const { error } = await (supabase as any)
      .from('user_privacy_settings')
      .upsert({
        user_id: childId,
        profile_visibility: settings.profileVisibility,
        show_email: settings.showContactPublic,
        show_phone: settings.showContactPublic,
        show_age: settings.showAge,
        allow_messages: settings.allowMessages,
        allow_team_invites: settings.allowTeamInvites,
        show_online_status: settings.showOnlineStatus,
        is_parent_managed: true,
        managed_by_parent_id: parentId,
        last_modified_by: parentId,
        last_modified_by_type: 'internal',
        updated_at: new Date().toISOString(),
      });

    if (error) {
      console.error('Error updating child privacy settings:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error updating child privacy settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if a user is under 13 based on their age group
 */
export const isUserUnder13 = (ageGroup: string | undefined): boolean => {
  if (!ageGroup) return false;
  
  const under13AgeGroups = [
    '13-and-under', '13 and under', 'under-13', 'under 13',
    '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'
  ];
  
  return under13AgeGroups.includes(ageGroup.toLowerCase());
};

export const childPrivacyService = {
  verifyParentChildConnection,
  getChildPrivacySettings,
  updateChildPrivacySettings,
  isUserUnder13,
};
